import { FC, useCallback, useEffect, useState } from 'react'
import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import VariableLeadField, {
  VariableLeadFieldSettings,
} from '../../../VariableLeadField/VariableLeadField'
import { Field, LeadField as CrmLeadFieldValue } from '@/api/types/crm/crm.types'
import { crmApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import classes from './index.module.css'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import {
  getLeadFieldCollapsed,
  resolveTextareaCollapsedDefault,
} from '@pages/Admin/CRM/utils/leadFields'
import { useOptionalLeadRefetch } from '../../LeadRefetchContext'

interface LeadFieldProps {
  field: Field
  value?: string
  onChange: (val: string) => void
  fieldSettings?: VariableLeadFieldSettings
  disabled?: boolean
  size?: 'small' | 'middle'
  tooltipTitle?: string
  /** Переопределяет placeholder (по умолчанию — field.name). */
  placeholder?: string
  /** id лида — для сохранения свёртки textarea без Save карточки */
  leadId?: number
  /** Явное состояние с лида; если нет — берём из lead context / settings */
  isCollapsed?: boolean
}

const TEXTAREA_COLLAPSE_HINT =
  'Состояние свёртки сохраняется для этого поля в карточке текущего лида и будет видно всем, кто откроет эту заявку.'

const LeadField: FC<LeadFieldProps> = ({
  field,
  value,
  onChange,
  fieldSettings,
  disabled,
  size = 'small',
  tooltipTitle,
  placeholder,
  leadId: leadIdProp,
  isCollapsed: isCollapsedProp,
}) => {
  const addAlert = useAddAlert()
  const leadRefetch = useOptionalLeadRefetch()
  const isReadOnlyLink = disabled && field.type === 'link'
  const trimmedLinkValue = (value ?? '').trim()
  const isTextareaField = field.type === 'textarea'

  const leadId = leadIdProp ?? leadRefetch?.lead?.id
  const collapsedFromLead =
    typeof isCollapsedProp === 'boolean'
      ? isCollapsedProp
      : getLeadFieldCollapsed(leadRefetch?.lead, field.unique_name)

  const resolveCollapsed = useCallback(() => {
    if (typeof collapsedFromLead === 'boolean') return collapsedFromLead
    return resolveTextareaCollapsedDefault(field.settings, field.is_collapsed)
  }, [collapsedFromLead, field.is_collapsed, field.settings])

  const [isTextareaCollapsed, setIsTextareaCollapsed] = useState(resolveCollapsed)
  const [isCollapseSaving, setIsCollapseSaving] = useState(false)

  useEffect(() => {
    setIsTextareaCollapsed(resolveCollapsed())
  }, [resolveCollapsed])

  const patchLeadCollapsed = useCallback(
    (nextCollapsed: boolean) => {
      const currentLead = leadRefetch?.lead
      if (!currentLead?.fields || !leadRefetch?.patchLead) return

      leadRefetch.patchLead({
        fields: currentLead.fields.map((leadField: CrmLeadFieldValue) => {
          const sameByUniqueName = leadField.unique_name === field.unique_name
          const sameByFieldId =
            leadField.field_id != null &&
            Number(leadField.field_id) === Number(field.id)
          if (!sameByUniqueName && !sameByFieldId) return leadField
          return { ...leadField, is_collapsed: nextCollapsed }
        }),
      })
    },
    [field.id, field.unique_name, leadRefetch],
  )

  const handleToggleTextareaCollapsed = useCallback(async () => {
    if (!isTextareaField || isCollapseSaving) return

    const nextCollapsed = !isTextareaCollapsed
    setIsTextareaCollapsed(nextCollapsed)

    if (!leadId) {
      patchLeadCollapsed(nextCollapsed)
      return
    }

    setIsCollapseSaving(true)
    try {
      const result = await crmApi.updateLeadFieldUi(
        { lead_id: Number(leadId) },
        {
          field_id: Number(field.id),
          is_collapsed: nextCollapsed,
        },
      )

      const savedCollapsed = result?.data?.data?.is_collapsed
      const resolved =
        typeof savedCollapsed === 'boolean' ? savedCollapsed : nextCollapsed

      setIsTextareaCollapsed(resolved)
      patchLeadCollapsed(resolved)
    } catch {
      setIsTextareaCollapsed(!nextCollapsed)
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось сохранить состояние поля',
        type: 'error',
      })
    } finally {
      setIsCollapseSaving(false)
    }
  }, [
    addAlert,
    field.id,
    isCollapseSaving,
    isTextareaCollapsed,
    isTextareaField,
    leadId,
    patchLeadCollapsed,
  ])

  const handleCopyLink = useCallback(async () => {
    if (!trimmedLinkValue) return

    try {
      await navigator.clipboard.writeText(trimmedLinkValue)
      addAlert({
        title: 'Успешно',
        text: 'Ссылка скопирована в буфер обмена',
        type: 'success',
      })
    } catch {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось скопировать ссылку',
        type: 'error',
      })
    }
  }, [addAlert, trimmedLinkValue])

  const variableField = (
    <VariableLeadField
      type={field.type}
      readOnly={isReadOnlyLink}
      textareaCollapsed={isTextareaCollapsed}
      fieldProps={{
        field,
        size,
        id: field.unique_name,
        name: field.unique_name,
        value: value ?? '',
        onChange: (e) => {
          if (disabled) return
          onChange(typeof e === 'string' ? e : e.target.value)
        },
        placeholder: placeholder ?? field.name,
      }}
      fieldSettings={fieldSettings}
    />
  )

  // Телефон с кнопками чатов (VK/MAX): внешний Tooltip + Dropdown → пустой белый бабл.
  // Подпись поля и так видна слева, отдельные тултипы у иконок.
  const skipOuterTooltip = Boolean(fieldSettings?.phoneNumber)

  const fieldControl =
    field.type === 'link' ? (
      <div className={classes.tooltipTrigger}>
        <div className={classes.linkFieldControl}>
          <div className={classes.linkFieldInput}>{variableField}</div>
          {trimmedLinkValue ? (
            <div className={classes.copyButtonWrap}>
              <Tooltip title="Скопировать ссылку" placement="top">
                <button
                  type="button"
                  className={classes.copyButton}
                  aria-label={`Скопировать ссылку ${field.name}`}
                  onClick={() => void handleCopyLink()}
                >
                  <CopyOutlined
                    className={classes.copyIcon}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </button>
              </Tooltip>
            </div>
          ) : null}
        </div>
      </div>
    ) : isTextareaField ? (
      <div className={classes.textareaFieldControl}>
        <div className={classes.textareaFieldToolbar}>
          <div className={classes.textareaFieldTitle}>{field.name}</div>
          <div className={classes.textareaFieldActions}>
            <Tooltip
              title={TEXTAREA_COLLAPSE_HINT}
              placement="top"
              overlayClassName={classes.tooltipOverlay}
            >
              <InfoCircleOutlined
                className={classes.textareaCollapseInfoIcon}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </Tooltip>
            <button
              type="button"
              className={classes.textareaToggleButton}
              disabled={isCollapseSaving}
              onClick={() => void handleToggleTextareaCollapsed()}
            >
              {isTextareaCollapsed ? 'Развернуть' : 'Свернуть'}
            </button>
          </div>
        </div>
        <div className={classes.textareaFieldInput}>{variableField}</div>
        <div className={classes.textareaLimitHint}>
          Максимум 10000 символов
        </div>
      </div>
    ) : (
      <div
        className={classes.tooltipTrigger}
        style={
          disabled
            ? {
                pointerEvents: 'none',
                userSelect: 'none',
              }
            : undefined
        }
      >
        {variableField}
      </div>
    )

  if (skipOuterTooltip) {
    return fieldControl
  }

  return (
    <TooltipWrapper
      key={field.id}
      title={
        isTextareaField ? undefined : tooltipTitle ?? field.name
      }
      placement="top"
      overlayClassName={classes.tooltipOverlay}
    >
      {fieldControl}
    </TooltipWrapper>
  )
}

export default LeadField
